import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ASSETS } from "../data";
import { useContent } from "../context/ContentContext";
import { scrollToId } from "../lib/scroll";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="reveal-mask">
    <motion.span custom={i} variants={line} initial="hidden" animate="visible" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onBuy, price }) => {
  const { content } = useContent();
  const h = content.hero || {};
  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20">
      {/* Falcon image with parallax feel */}
      <motion.div
        initial={{ scale: 1.15, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="absolute top-0 right-0 h-full w-full lg:w-[55%]"
      >
        <img src={ASSETS.heroFalcon} alt="Falcon" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent lg:bg-gradient-to-r lg:from-[#050505] lg:via-[#050505]/70 lg:to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-[6px] w-[6px] bg-accent rounded-full animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">{h.badge}</span>
        </motion.div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-[clamp(3rem,10vw,9rem)]">
          <Line i={0}>{h.titleTop}</Line>
          <Line i={1} className="text-white/30">{h.titleMuted}</Line>
          <Line i={2}>{h.titleLead}<span className="text-accent"> {h.titleAccent}</span></Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 max-w-xl font-body text-base sm:text-lg text-white/60 leading-relaxed"
        >
          {h.subtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6"
        >
          <button
            data-testid="hero-buy-button"
            onClick={onBuy}
            className="group relative inline-flex items-center gap-3 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-9 py-5 overflow-hidden"
          >
            <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            <span className="relative z-10 group-hover:text-black transition-colors duration-300">
              Get The Pack {price ? `· ${price}` : ""}
            </span>
            <ArrowUpRight size={18} className="relative z-10 group-hover:text-black transition-colors duration-300 group-hover:rotate-45 transition-transform" />
          </button>
          <div className="flex flex-col gap-2.5">
            <button
              data-testid="hero-free-tips-link"
              onClick={() => scrollToId("#free-tips")}
              className="font-mono text-xs uppercase tracking-[0.2em] text-white/55 hover:text-accent transition-colors text-left underline underline-offset-4 decoration-white/20"
            >
              Not ready? Get 5 free tips first →
            </button>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">
              {h.ctaNote}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
